import { useEffect, useMemo, useState } from 'react';
import { ApiError, useRoute, useSession } from '@huaca/ui';
import { getInstance, updateInstanceSnapshot } from '../api';
import { validateConditionRefs } from '@huaca/claro-engine';
import type { Instance, TemplateBody } from '../types';
import { FormBuilderBody } from './FormBuilderBody';

/**
 * Per-instance snapshot editing: the Editor refines ONE onboarding's own form after the link is sent,
 * with zero effect on the master template or any other onboarding. It shares the whole editing surface
 * with TemplateBuilder (FormBuilderBody); only the load (getInstance → its snapshot), the save
 * (updateInstanceSnapshot), the permission (claro:instance:editSchema), and the return target differ.
 * No firm-logo control — that is firm-wide, not per-onboarding.
 *
 * Saving may downgrade completion server-side (a newly required/revealed field reopens a completed
 * onboarding) and orphan removed fields' answers; we simply navigate back to /instances/:id, which
 * remounts the dashboard with this client selected and refetches, so those changes appear at once.
 */
export function SnapshotEditor({ instanceId }: { instanceId: string }) {
  const { client, can } = useSession();
  const { navigate } = useRoute();
  const mayEdit = can('claro:instance:editSchema');

  const [instance, setInstance] = useState<Instance | null>(null);
  const [body, setBody] = useState<TemplateBody | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    getInstance(client, instanceId)
      .then((i) => {
        if (!live) return;
        setInstance(i);
        setBody({ name: i.snapshot.name, sections: i.snapshot.sections });
      })
      .catch(() => live && setLoadError('לא ניתן לטעון את הטופס.'));
    return () => {
      live = false;
    };
  }, [client, instanceId]);

  const refErrors = useMemo(() => (body ? validateConditionRefs(body) : []), [body]);
  const back = () => navigate(`/instances/${instanceId}`);

  if (!mayEdit) {
    return (
      <div className="huaca-state" data-testid="snapshot-forbidden">
        <h2>אין לכם הרשאת עריכה</h2>
        <p>רק עורך בארגון יכול לערוך את טופס הקליטה של לקוח.</p>
        <button type="button" className="btn-primary" onClick={() => navigate('/')}>
          חזרה
        </button>
      </div>
    );
  }
  if (loadError) return <p role="alert">{loadError}</p>;
  if (!body || !instance) return <p>טוען…</p>;

  async function save() {
    if (!body) return;
    setSaveError(null);
    if (body.name.trim() === '') return setSaveError('יש לתת שם לטופס.');
    if (refErrors.length > 0) return setSaveError('יש תנאי שמפנה לשדה שאינו מוגדר לפניו. תקנו אותו לפני שמירה.');
    setSaving(true);
    try {
      await updateInstanceSnapshot(client, instanceId, body);
      back();
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : 'השמירה נכשלה. נסו שוב.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="claro-builder claro-page" data-testid="snapshot-editor">
      <div className="claro-page-head">
        <h1>
          עריכת טופס הקליטה — <bdi>{instance.client.fullName}</bdi>
        </h1>
        <div className="claro-head-actions">
          <button type="button" onClick={back}>
            ביטול
          </button>
          <button type="button" className="btn-primary" disabled={saving} onClick={() => void save()} data-testid="save-snapshot">
            {saving ? 'שומר…' : 'שמירה'}
          </button>
        </div>
      </div>

      <p className="claro-hint">
        שינויים כאן משפיעים על הטופס של הלקוח הזה בלבד — לא על התבנית או על לקוחות אחרים. תשובות שכבר מולאו נשמרות.
      </p>

      {saveError && <p role="alert">{saveError}</p>}

      <div className="huaca-field">
        <label htmlFor="snap-name">שם הטופס</label>
        <input id="snap-name" value={body.name} onChange={(e) => setBody({ ...body, name: e.target.value })} />
      </div>

      <FormBuilderBody body={body} onChange={setBody} />
    </div>
  );
}
